import { formatPrice } from "@/utilities";
import { Form, Input, Alert, Divider, Radio } from "antd";
import { Flex, Typography } from "antd";
import { t } from "i18next";

interface Props {
  dataPayment?: any;
  selectedPayment?: string;
}

const { Text } = Typography;

const CreditCardForm = ({ dataPayment, selectedPayment }: Props) => {
  const [form] = Form.useForm();

  // Determinar el payment_method de MercadoPago
  const mercadoPagoPaymentMethod =
    dataPayment?.metadata?.paymentResponse?.payment_type_id ||
    dataPayment?.metadata?.payment_type_id ||
    "";
  const isMpCardPayment =
    selectedPayment === "mercadopago_basic" &&
    (mercadoPagoPaymentMethod === "credit_card" || mercadoPagoPaymentMethod === "debit_card");
  const isFirstdataCardPayment =
    selectedPayment === "firstdata" &&
    !!(dataPayment?.metadata?.cardnumber || dataPayment?.metadata?.ipgTransactionId);
  const isCardPayment = isMpCardPayment || isFirstdataCardPayment;

  // Obtener datos específicos según el gateway
  const getInstallmentsInfo = () => {
    if (selectedPayment === "firstdata") {
      // Para Fiserv
      return {
        numberOfInstallments: dataPayment?.metadata?.installmentsNumber || 1,
        installmentsInterest: dataPayment?.metadata?.installmentsInterest || 0,
        installmentsInterestPercent: dataPayment?.metadata?.installmentsInterestPorcent || 0,
      };
    } else if (selectedPayment === "mercadopago_basic") {
      // Para MercadoPago
      const installments = dataPayment?.metadata?.installments || 1;
      const installmentAmount = dataPayment?.metadata?.paymentResponse?.transaction_details?.installment_amount || 0;

      return {
        numberOfInstallments: installments,
        installmentAmount: installmentAmount,
        paymentMethod: mercadoPagoPaymentMethod,
      };
    }
    return null;
  };

  const installmentsInfo = getInstallmentsInfo();

  // Mapeo de payment_method de MercadoPago a texto legible
  const getMercadoPagoMethodLabel = (method: string) => {
    const methodLabels: Record<string, string> = {
      account_money: "Dinero en cuenta",
      ticket: "Pago Fácil / Rapipago",
      credit_card: "Tarjeta de crédito",
      debit_card: "Tarjeta de débito",
      digital_currency: "Cuotas sin tarjeta (Mercado Crédito)",
    };
    return methodLabels[method] || method;
  };

  // Más compacto que el resto del pago (Diseño, 2026-10-19): 4px entre filas de datos y
  // 12px a cada lado de la línea, en vez de 8 y 24.
  return (
    <Flex vertical gap={4}>
      {dataPayment === "" ? (
        <Alert
          message="Waiting for user payment and Gateway response."
          banner
        />
      ) : (
        <Radio.Group defaultValue="manual" style={{ width: "100%" }}>
          <Flex gap={50}>
            <Radio value="api" disabled>
              {t("orders.clearsabe.text")}
            </Radio>
            <Radio value="manual" checked>
              {t("orders.label.manualConfirm")}
            </Radio>
          </Flex>
        </Radio.Group>
      )}

      <Flex gap={16}>
        <Flex vertical style={{ width: 350 }}>
          <Text type="secondary">{t("orders.label.paymentnumber")}</Text>
          <Text>
            {selectedPayment === "mercadopago_basic"
              ? dataPayment?.metadata?.paymentResponse?.id || "-"
              : dataPayment?.paymentId || "-"}
          </Text>
        </Flex>
        <Flex vertical>
          <Text type="secondary">{t("orders.label.amount")}</Text>
          <Text>{formatPrice(dataPayment?.amount) || "-"}</Text>
        </Flex>
      </Flex>

      {selectedPayment === "mercadopago_basic" && (
        <>
          <Flex gap={16}>
            <Flex vertical style={{ width: 350 }}>
              <Text type="secondary">{t("orders.label.mercadopagostatus")}</Text>
              <Text>{dataPayment?.metadata?.paymentResponse?.status || "-"}</Text>
            </Flex>
            <Flex vertical>
              <Text type="secondary">{t("orders.label.accreditationstatus")}</Text>
              <Text>{dataPayment?.metadata?.paymentResponse?.status_detail || "-"}</Text>
            </Flex>
          </Flex>

          <Flex gap={16}>
            <Flex vertical style={{ width: 350 }}>
              <Text type="secondary">{t("orders.label.paymentprocessingstatus")}</Text>
              <Text>{dataPayment?.metadata?.paymentResponse?.captured ? "Captured" : "-"}</Text>
            </Flex>
          </Flex>
        </>
      )}

      {isFirstdataCardPayment && (
        <>
          <Flex gap={16}>
            <Flex vertical style={{ width: 350 }}>
              <Text type="secondary">{t("orders.label.fiservstatus")}</Text>
              <Text>{dataPayment?.metadata?.status || "-"}</Text>
            </Flex>
            <Flex vertical>
              <Text type="secondary">Modo de Pago</Text>
              <Text>{dataPayment?.metadata?.pay_mode || "-"}</Text>
            </Flex>
          </Flex>

          <Flex gap={16}>
            <Flex vertical style={{ width: 350 }}>
              <Text type="secondary">Tipo de Tarjeta</Text>
              <Text>{({
                V: "Visa",
                M: "Mastercard",
                MA: "Maestro",
                A: "American Express",
                NARANJA: "Naranja",
              } as Record<string, string>)[dataPayment?.metadata?.card_type] || dataPayment?.metadata?.card_type || "-"}</Text>
            </Flex>
            {dataPayment?.metadata?.installmentDelayMonths && (
              <Flex vertical>
                <Text type="secondary">Meses de Diferimiento</Text>
                <Text>{dataPayment.metadata.installmentDelayMonths}</Text>
              </Flex>
            )}
          </Flex>
        </>
      )}

      <Divider style={{ marginBlock: 12 }} />

      <Flex gap={16}>
        <Flex vertical style={{ width: 350 }}>
          <Text type="secondary">{t("orders.label.cardnumer")}</Text>
          <Text>
            {dataPayment?.metadata?.paymentResponse?.card?.last_four_digits
              ? `**** **** **** ${dataPayment.metadata.paymentResponse.card.last_four_digits}`
              : dataPayment?.metadata?.cardNumber || "-"}
          </Text>
        </Flex>
        {selectedPayment === "mercadopago_basic" &&
          dataPayment?.metadata?.paymentResponse?.card?.expiration_month &&
          dataPayment?.metadata?.paymentResponse?.card?.expiration_year && (
            <Flex vertical>
              <Text type="secondary">{t("orders.label.expirydate")}</Text>
              <Text>
                {`${dataPayment.metadata.paymentResponse.card.expiration_month}/${dataPayment.metadata.paymentResponse.card.expiration_year}`}
              </Text>
            </Flex>
          )}
      </Flex>

      <Flex gap={16}>
        <Flex vertical style={{ width: 350 }}>
          <Text type="secondary">Nombre</Text>
          <Text>
            {selectedPayment === "mercadopago_basic" && dataPayment?.metadata?.paymentResponse?.card?.cardholder?.name
              ? dataPayment.metadata.paymentResponse.card.cardholder.name
              : dataPayment?.metadata?.paymentResponse?.payer?.first_name &&
                dataPayment?.metadata?.paymentResponse?.payer?.last_name
                ? `${dataPayment.metadata.paymentResponse.payer.first_name} ${dataPayment.metadata.paymentResponse.payer.last_name}`
                : dataPayment?.metadata?.name || "-"}
          </Text>
        </Flex>
        {selectedPayment === "firstdata" && (
          <Flex vertical>
            <Text type="secondary">DNI</Text>
            <Text>{dataPayment?.metadata?.dni || "-"}</Text>
          </Flex>
        )}
        {isMpCardPayment && dataPayment?.metadata?.paymentResponse?.card?.cardholder?.identification && (
          <Flex vertical>
            <Text type="secondary">
              {dataPayment.metadata.paymentResponse.card.cardholder.identification.type || "ID"}
            </Text>
            <Text>{dataPayment.metadata.paymentResponse.card.cardholder.identification.number || "-"}</Text>
          </Flex>
        )}
      </Flex>

      {selectedPayment === "mercadopago_basic" && (
        <Flex gap={16}>
          <Flex vertical style={{ width: 350 }}>
            <Text type="secondary">Email</Text>
            <Text>{dataPayment?.metadata?.paymentResponse?.payer?.email || "-"}</Text>
          </Flex>
        </Flex>
      )}

      <Flex gap={16}>
        <Flex vertical style={{ width: 350 }}>
          <Text type="secondary">{t("common.payment.method")}</Text>
          <Text>
            {selectedPayment === "mercadopago_basic"
              ? getMercadoPagoMethodLabel(mercadoPagoPaymentMethod)
              : dataPayment?.metadata?.method_title || "-"}
          </Text>
        </Flex>
        {selectedPayment === "mercadopago_basic" && (
          <Flex vertical>
            <Text type="secondary">Método de Pago ID</Text>
            <Text>{dataPayment?.metadata?.payment_method || dataPayment?.metadata?.paymentResponse?.payment_method_id || "-"}</Text>
          </Flex>
        )}
        {selectedPayment === "firstdata" && (
          <Flex vertical>
            <Text type="secondary">{t("orders.label.installments")}</Text>
            {installmentsInfo && (
              <Text>
                {`${installmentsInfo.numberOfInstallments} cuota${installmentsInfo.numberOfInstallments > 1 ? 's' : ''} con ${Math.round(installmentsInfo.installmentsInterestPercent)}% de interés`}
                {installmentsInfo.installmentsInterest > 0 && (
                  <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    (Interés financiado: {formatPrice(installmentsInfo.installmentsInterest)})
                  </Text>
                )}
              </Text>
            )}
          </Flex>
        )}
      </Flex>

      <Flex gap={16}>
        <Flex vertical style={{ width: 350 }}>
          <Text type="secondary">{t("orders.label.installments")}</Text>
          {selectedPayment === "mercadopago_basic" && installmentsInfo && (
            <Text>
              {installmentsInfo.numberOfInstallments > 1
                ? `${installmentsInfo.numberOfInstallments} cuotas de ${formatPrice(installmentsInfo.installmentAmount)}`
                : "Pago en 1 cuota"}
            </Text>
          )}
        </Flex>
        {selectedPayment === "mercadopago_basic" && dataPayment?.metadata?.paymentResponse?.card?.bin && (
          <Flex vertical>
            <Text type="secondary">BIN de Tarjeta</Text>
            <Text>{dataPayment.metadata.paymentResponse.card.bin || "-"}</Text>
          </Flex>
        )}
      </Flex>

      {selectedPayment === "mercadopago_basic" && dataPayment?.metadata?.paymentResponse?.authorization_code && (
        <Flex gap={16}>
          <Flex vertical style={{ width: 350 }}>
            <Text type="secondary">Código de Autorización</Text>
            <Text>{dataPayment.metadata.paymentResponse.authorization_code}</Text>
          </Flex>
          <Flex vertical>
            <Text type="secondary">Referencia Externa</Text>
            <Text>{dataPayment.metadata.paymentResponse.external_reference || "-"}</Text>
          </Flex>
        </Flex>
      )}


    </Flex>
  );
};

export default CreditCardForm;